import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';

export interface SortOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  /** Text shown on the collapsed filter control (e.g. "All triggers" / "2 selected"). */
  label: string;
  options: { value: string; label: string; checked: boolean }[];
  onToggle: (value: string) => void;
}

interface Props {
  sortValue: string;
  sortOptions: SortOption[];
  onSortChange: (value: string) => void;
  /** When omitted, only the sort control renders. */
  filter?: FilterConfig;
}

const selectSx = {
  'fontSize': '0.78rem',
  'color': 'text.secondary',
  'fontWeight': 500,
  '& .MuiSelect-select': { py: 0, pr: '20px !important' },
  '& .MuiSvgIcon-root': { fontSize: 16, color: 'rgba(0,0,0,0.25)' },
} as const;

/**
 * Compact sort + optional multi-select filter bar, right-aligned above a card
 * grid. Shared by the servers and workflows lists so both share an identical
 * look and feel.
 */
export default function ListSortFilterBar({ sortValue, sortOptions, onSortChange, filter }: Props) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <SortIcon sx={{ fontSize: 15, color: 'rgba(0,0,0,0.25)' }} />
      <Select
        size="small"
        value={sortValue}
        onChange={(e) => onSortChange(e.target.value)}
        variant="standard"
        disableUnderline
        sx={selectSx}
      >
        {sortOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.82rem' }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {filter && (
        <>
          <Box sx={{ width: '1px', height: 14, bgcolor: 'rgba(0,0,0,0.1)', mx: 0.5 }} />
          <FilterListIcon sx={{ fontSize: 15, color: 'rgba(0,0,0,0.25)' }} />
          <Select
            size="small"
            multiple
            value={filter.options.filter((o) => o.checked).map((o) => o.value)}
            renderValue={() => filter.label}
            variant="standard"
            disableUnderline
            sx={selectSx}
          >
            {filter.options.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                onClick={() => filter.onToggle(opt.value)}
                sx={{ py: 0.5 }}
              >
                <Checkbox
                  size="small"
                  checked={opt.checked}
                  sx={{ p: 0.5, mr: 1, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                />
                <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </MenuItem>
            ))}
          </Select>
        </>
      )}
    </Box>
  );
}
